import instance, { isAxiosErrorHandled, isErrorHandled } from 'api';
import Button from 'components/button/Button';
import { TFAField } from 'components/form/TFAField';
import { Modal } from 'components/modal/Modal';
import { endpoints } from 'endpoints.config';
import { getErrorMessage } from 'errors';
import { Form, Formik, FormikHelpers } from 'formik';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { useTFAField } from 'helpers/useTFAField';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, selectModalState, ModalTypes } from 'reducers/modal';
import * as Yup from 'yup';
import { TFAType } from 'pages/register/models';
import { Toast } from 'helpers/toast';
import { useGetAccountDetailsForCrypto } from 'helpers/useGetAccountDetails';
import { Spinner } from 'components/spinner/Spinner';

export type NewOrderDetail = {
    commision: number,
    feeGroupRate: number,
    netwrokFee: number,
    totalPrice: number
}

type ReviewModal = {
    tfaCode: '';
    tfaType: TFAType;
    destinationCustomerAssetAccountID: number | null;
    sourceCustomerAssetAccountID: number | null;
    quantityType: 'Amount' | 'Price';
    asset: 'dUSD';
    amount: string;
    price: string;
};

const generateInitialValues = (
    dUsdAccountId: number,
    sourceAssetAccountId: number
): ReviewModal => ({
    amount: '',
    price: '',
    tfaCode: '',
    tfaType: 'AuthenticatorApp',
    quantityType: 'Amount',
    asset: 'dUSD',
    destinationCustomerAssetAccountID: dUsdAccountId,
    sourceCustomerAssetAccountID: sourceAssetAccountId,
});


const validationSchemaStep2 = Yup.object({
    tfaCode: Yup.string()
        .required('Please enter your TFA code')
        .min(6, 'Please enter your TFA code'),
});

export const ReviewModal = () => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState('');
    const [tfaType, toggleTfaType] = useTFAField();

    const [orderDetails, setOrderDetails] = useState<NewOrderDetail | null>(null);
    const modalData = useSelector(selectModalState);
    const [loading, setLoading] = useState(false);
    const [showTfa, setShowTfa] = useState(false)

    useEffect(() => {
        const data = (modalData as { data: any }).data;
        if (data) {
            setLoading(true);
            instance
                .post(endpoints.cards.ReviewTransferFunds, data.payload)
                .then((res) => {
                    setOrderDetails(res.data.details);
                })
                .catch((err) => {
                    if (isErrorHandled(err) && err.response.data?.errors?.length > 0) {
                        setErrorMessage(getErrorMessage(err.response.data.errors[0].messageCode));
                    } else {
                        setErrorMessage(getErrorMessage('Generic'));
                    }
                    console.error(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [modalData]);



    const dusdAccountDetails = useGetAccountDetailsForCrypto('dUSD');

    const initialValues = useMemo(
        () =>
            generateInitialValues(
                dusdAccountDetails?.id ?? 0,
                modalData.modalType === ModalTypes.BUY_DUSD
                    ? modalData.data.sourceAssetAccount.id
                    : 0
            ),
        [dusdAccountDetails?.id, modalData]
    );
    const handleSubmit = (
        _values: any,
        { setSubmitting }: FormikHelpers<any>
    ) => {
        const data = (modalData as { data: any }).data;
        const finalPayload = {
            ...data.payload,
            tfaDetails: {
                tfaCode: _values.tfaCode,
                tfaType: _values.tfaType
            }
        }
        setErrorMessage('');
        instance
            .post(endpoints.cards.transferfunds, finalPayload)
            .then(() => {
                Toast.openSuccessToast('Top up successful');
                dispatch(closeModal());
                data.setIsModalClosed(true)
            })
            .catch((err) => {
                if (isAxiosErrorHandled(err)) {
                    const errors = err.response.data.errors;
                    if (errors.some((error) => toCamelCase(error.fieldName) in initialValues)) {
                        console.error(errors);
                    } else {
                        Toast.openErrorToast(getErrorMessage(errors[0].messageCode));
                    }
                } else {
                    Toast.openGenericErrorToast();
                }
            })
            .finally(() => setSubmitting(false));
    };

    const createModalButtons = (submitfn: () => void, submitting: boolean) => (
        <div className="ModalNavigation">
            <Button onClick={() => dispatch(closeModal())} priority="secondary">
                Close
            </Button>
            <Button onClick={(e) => {
                if (!showTfa) {
                    e.preventDefault();
                    if (!submitting) setShowTfa(true);
                } else {
                    submitfn()
                }
            }} priority="primary" disabled={submitting}>
                {submitting ? <Spinner /> : 'Top Up'}
            </Button>
        </div>
    );

    if (modalData.modalType !== ModalTypes.REVIEW_MODAL) return null;

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchemaStep2}
            enableReinitialize
        >
            {({ submitForm, isSubmitting, values, setFieldValue }) => {
                return (
                    <Modal
                        title={
                            <>
                                Review Your Top Up
                            </>
                        }
                        customButtons={createModalButtons(submitForm, isSubmitting)}
                        closeOnClickOutside={false}
                    >
                            <div className="ReviewModal">
                                {loading && <Spinner />}
                                {!loading && orderDetails && (
                                    <Form className="ContentBox LeftAlign">
                                        <h2>{`Order Summary`}</h2>
                                        <div className="OrderSummaryContainer">

                                            <div className="SummarySection">
                                                <p>
                                                    Network fee <span className="FloatRight">{orderDetails?.netwrokFee}</span>
                                                </p>
                                            </div>
                                            <div className="SummarySection">
                                                <p>
                                                    Commission <span className="FloatRight">{orderDetails?.commision}</span>
                                                </p>
                                            </div>
                                            <p>
                                                <b>
                                                    Total due{" "}
                                                </b>
                                                <span className="FloatRight">
                                                    {`${orderDetails?.totalPrice} `}
                                                </span>
                                            </p>
                                            <p>
                                                <b>Transfer type</b>{' '}
                                                <span className="FloatRight">{modalData.data.transferTypes}</span>
                                            </p>

                                        </div>
                                        {showTfa && <TFAField
                                            field={'tfaCode'}
                                            label={'TFA Code'}
                                            required={true}
                                            toggleTFAType={
                                                toggleTfaType
                                                    ? () => {
                                                        toggleTfaType();
                                                        setFieldValue(
                                                            'tfaType',
                                                            values.tfaType
                                                        );
                                                    }
                                                    : null
                                            }
                                            tfaType={tfaType}
                                            holdFocus
                                            autoFocus
                                        />}
                                    </Form>

                                )}
                            </div>
                            {errorMessage && <div className="ErrorText">{errorMessage}</div>}
                            <button type="submit" style={{ display: 'none' }} />
                    </Modal>
                );
            }}
        </Formik>
    );
};